import {
  CheckCircleIcon,
  CircleNotchIcon,
  ClockIcon,
  CoinsIcon,
  SparkleIcon,
  StarIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
} from "framer-motion";
import {
  useCallback,
  useEffect,
  useState,
} from "react";

import { useAiPacks } from "@/client/services/ai-credit/api";
import { useCrBalance } from "@/client/services/credit/balance";
import { useAccountCurrency } from "@/client/services/currency/currency";
import { useUser } from "@/client/services/user";
import { useDialog } from "@/client/stores/dialog";

import { AccountCurrencyNote } from "./forfait/account-currency-note";

type AiPackReason =
  | "scoring"
  | "translate"
  | "improve";

type AiPacksStep =
  | "checking"
  | "packs"
  | "error";

const DEFAULT_FEATURES = [
  "Analyse ATS et recommandations ciblées",
  "Amélioration professionnelle des textes",
  "Traduction complète du CV",
  "Facturation selon l’utilisation réelle",
];

const parseFeatures = (
  description?: string | null,
) => {
  const features =
    description
      ?.split(/[,;\n]/)
      .map((feature) => feature.trim())
      .filter(Boolean) ?? [];

  return features.length > 0
    ? features
    : DEFAULT_FEATURES;
};

const getReasonMessage = (
  reason?: AiPackReason,
) => {
  switch (reason) {
    case "scoring": {
      return "Rechargez vos crédits pour reprendre l’analyse ATS de votre CV.";
    }

    case "translate": {
      return "Rechargez vos crédits pour traduire votre CV dans la langue choisie.";
    }

    case "improve": {
      return "Rechargez vos crédits pour générer une nouvelle suggestion professionnelle.";
    }

    default: {
      return "Sélectionnez le pack de crédits IA adapté à vos besoins.";
    }
  }
};

const getErrorMessage = (
  error: unknown,
): string => {
  if (error instanceof Error) {
    return error.message;
  }

  return "Impossible de vérifier votre forfait. Veuillez réessayer.";
};

export const AiPacksDialog = () => {
  const reduceMotion = useReducedMotion();

  const {
    isOpen,
    close,
    payload,
  } = useDialog<{
    type?: AiPackReason;
  }>("ai-packs");

  const { open: openPayment } =
    useDialog("payment");

  const { open: openCvOffers } =
    useDialog("forfait");

  const {
    user,
    loading: userLoading,
    error: userError,
  } = useUser();

  const reason = payload?.item?.type;

  const {
    refetch: refreshDownloadBalance,
  } = useCrBalance(user?.id);

  const [step, setStep] =
    useState<AiPacksStep>("checking");

  const [
    accessError,
    setAccessError,
  ] = useState<string | null>(null);

  const [
    selectedPackId,
    setSelectedPackId,
  ] = useState<string | null>(null);

  const { format: formatPrice } =
    useAccountCurrency();

  /*
   * Les packs ne sont chargés que lorsque
   * toutes les conditions sont validées.
   */
  const packs = useAiPacks(
    isOpen && step === "packs",
  );

  const openRequiredCvOffers = useCallback(() => {
    close();

    openCvOffers("create", {
      id: "forfait",
      item: {
        type: "creation",

        /*
         * Cette information peut servir à
         * revenir vers l’action IA après
         * l’achat d’une offre CV.
         */
        returnTo:
          reason ?? "ai-credits",
      },
    });
  }, [
    close,
    openCvOffers,
    reason,
  ]);

  const verifyAccess =
    useCallback(async () => {
      setAccessError(null);
      setStep("checking");

      if (userLoading) {
        return;
      }

      if (userError) {
        setAccessError(
          "Impossible de récupérer votre compte. Vérifiez votre connexion puis réessayez.",
        );

        setStep("error");
        return;
      }

      if (!user?.id) {
        setAccessError(
          "Votre session est introuvable. Veuillez vous reconnecter.",
        );

        setStep("error");
        return;
      }

      try {
        /*
         * Cette route calcule la somme des
         * Credit.remain encore utilisables.
         */
        const result =
          await refreshDownloadBalance();

        if (result.isError) {
          throw result.error;
        }

        const remainingDownloads = Number(
          result.data?.credit ?? 0,
        );

        /*
         * Sans crédit de téléchargement CV,
         * les packs IA ne sont pas accessibles.
         */
        if (remainingDownloads <= 0) {
          openRequiredCvOffers();
          return;
        }

        setStep("packs");
      } catch (error: unknown) {
        setAccessError(
          getErrorMessage(error),
        );

        setStep("error");
      }
    }, [
      openRequiredCvOffers,
      refreshDownloadBalance,
      user?.id,
      userError,
      userLoading,
    ]);

  /*
   * La vérification est relancée à chaque
   * ouverture du dialogue.
   */
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void verifyAccess();
  }, [
    isOpen,
    verifyAccess,
  ]);

  const choosePack = async (
    packId: string,
  ) => {
    if (
      !user?.id ||
      selectedPackId
    ) {
      return;
    }

    setAccessError(null);
    setSelectedPackId(packId);

    try {
      /*
       * Nouvelle vérification juste avant
       * le paiement afin d’éviter qu’une
       * ancienne valeur autorise l’achat.
       */
      const result =
        await refreshDownloadBalance();

      if (result.isError) {
        throw result.error;
      }

      const remainingDownloads = Number(
        result.data?.credit ?? 0,
      );

      if (remainingDownloads <= 0) {
        setSelectedPackId(null);
        openRequiredCvOffers();
        return;
      }

      close();

      openPayment("create", {
        id: "payment",
        item: {
          id: packId,
          source: "ai-credits",
          returnTo:
            reason ?? "ai-credits",
        },
      });
    } catch (error: unknown) {
      setAccessError(
        getErrorMessage(error),
      );

      setSelectedPackId(null);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setAccessError(null);
          setSelectedPackId(null);
          close();
        }
      }}
    >
      <DialogContent
        className={[
          "max-h-[calc(100dvh-1rem)]",
          "w-[calc(100vw-1rem)]",
          "max-w-5xl overflow-y-auto",
          "rounded-2xl border-0",
          "bg-[#f8f7ff] p-4",
          "shadow-2xl",
          "sm:w-[calc(100vw-2rem)]",
          "sm:p-7",
          "[scrollbar-color:#6366f1_#eef2ff]",
          "[scrollbar-width:thin]",
          "[&::-webkit-scrollbar]:w-1.5",
          "[&::-webkit-scrollbar-track]:bg-indigo-50",
          "[&::-webkit-scrollbar-thumb]:rounded-full",
          "[&::-webkit-scrollbar-thumb]:bg-indigo-400",
        ].join(" ")}
      >
        <AnimatePresence
          mode="wait"
          initial={false}
        >
          {step === "checking" ? (
            <motion.div
              key="checking"
              className="grid min-h-[320px] place-items-center"
              initial={
                reduceMotion
                  ? false
                  : {
                      opacity: 0,
                      scale: 0.98,
                    }
              }
              animate={{
                opacity: 1,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                scale: 0.98,
              }}
            >
              <div className="text-center">
                <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-gradient-to-br from-indigo-100 to-emerald-100 text-indigo-700">
                  <CircleNotchIcon
                    aria-hidden
                    size={27}
                    weight="bold"
                    className="animate-spin"
                  />
                </span>

                <DialogTitle className="mt-4 text-xl font-bold tracking-tight text-[#071451]">
                  Vérification de votre forfait
                </DialogTitle>

                <DialogDescription className="mt-2 text-xs leading-5 text-slate-500">
                  Nous vérifions vos
                  téléchargements disponibles.
                </DialogDescription>
              </div>
            </motion.div>
          ) : step === "error" ? (
            <motion.div
              key="error"
              className="grid min-h-[320px] place-items-center"
              initial={{
                opacity: 0,
                y: 10,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
            >
              <div className="max-w-md text-center">
                <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-rose-100 text-rose-600">
                  <WarningCircleIcon
                    aria-hidden
                    size={28}
                    weight="duotone"
                  />
                </span>

                <DialogTitle className="mt-4 text-xl font-bold text-[#071451]">
                  Vérification impossible
                </DialogTitle>

                <DialogDescription className="mt-2 text-sm leading-6 text-slate-600">
                  {accessError}
                </DialogDescription>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    void verifyAccess();
                  }}
                  className="mt-5 rounded-xl"
                >
                  Réessayer
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="packs"
              initial={
                reduceMotion
                  ? false
                  : {
                      opacity: 0,
                      x: 16,
                    }
              }
              animate={{
                opacity: 1,
                x: 0,
              }}
              exit={{
                opacity: 0,
                x: 10,
              }}
              transition={{
                duration: 0.3,
                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              }}
            >
              <header className="mx-auto max-w-2xl text-center">
                <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-gradient-to-br from-indigo-100 to-emerald-100 text-indigo-700">
                  <SparkleIcon
                    aria-hidden
                    size={24}
                    weight="duotone"
                  />
                </span>

                <DialogTitle className="mt-4 text-2xl font-bold tracking-tight text-[#071451] sm:text-3xl">
                  Rechargez vos crédits IA
                </DialogTitle>

                <DialogDescription className="mx-auto mt-2 max-w-xl text-xs leading-5 text-slate-600 sm:text-sm sm:leading-6">
                  {getReasonMessage(reason)}
                </DialogDescription>
              </header>

              <div className="mt-5">
                <AccountCurrencyNote
                  onNavigate={close}
                />
              </div>

              <div className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-indigo-100 bg-white px-4 py-3 text-xs font-semibold text-indigo-800">
                <CoinsIcon
                  aria-hidden
                  size={18}
                  weight="duotone"
                  className="text-emerald-500"
                />

                Packs sans abonnement et
                activation après paiement
              </div>

              {accessError && (
                <div
                  role="alert"
                  className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-center text-xs font-semibold leading-5 text-rose-700"
                >
                  {accessError}
                </div>
              )}

              {packs.isPending ? (
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {[0, 1, 2].map(
                    (item) => (
                      <div
                        key={item}
                        className="h-80 animate-pulse rounded-2xl bg-white"
                      />
                    ),
                  )}
                </div>
              ) : packs.isError ? (
                <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-10 text-center">
                  <p className="text-sm font-bold text-rose-700">
                    Impossible de charger les
                    packs IA.
                  </p>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      void packs.refetch();
                    }}
                    className="mt-4 rounded-xl"
                  >
                    Réessayer
                  </Button>
                </div>
              ) : !packs.data?.length ? (
                <div className="mt-6 rounded-2xl border border-dashed border-indigo-200 bg-white px-5 py-14 text-center text-sm text-slate-500">
                  Aucun pack IA disponible pour
                  le moment.
                </div>
              ) : (
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {packs.data.map(
                    (pack, index) => {
                      const features =
                        parseFeatures(
                          pack.description,
                        );

                      const recommended =
                        Boolean(
                          pack.isRecommended,
                        );

                      const selecting =
                        selectedPackId ===
                        pack.id;

                      return (
                        <motion.article
                          key={pack.id}
                          initial={{
                            opacity: 0,
                            y: 10,
                          }}
                          animate={{
                            opacity: 1,
                            y: 0,
                          }}
                          transition={{
                            delay: Math.min(
                              index * 0.05,
                              0.2,
                            ),
                          }}
                          className={cn(
                            "relative flex min-h-[350px] flex-col rounded-2xl border bg-white p-5 transition",
                            "hover:-translate-y-1 hover:shadow-xl",
                            recommended
                              ? "border-indigo-500 ring-2 ring-indigo-100"
                              : "border-indigo-100",
                          )}
                        >
                          {recommended && (
                            <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-indigo-700 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide text-white">
                              <StarIcon
                                aria-hidden
                                size={10}
                                weight="fill"
                                className="text-amber-300"
                              />

                              Recommandé
                            </span>
                          )}

                          <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-600">
                            Pack IA
                          </p>

                          <h2 className="mt-2 pr-20 text-lg font-bold text-[#071451]">
                            {pack.name}
                          </h2>

                          <p className="mt-3 text-3xl font-black tracking-tight text-[#071451]">
                            {formatPrice(
                              pack.price,
                            )}
                          </p>

                          <div className="mt-4 rounded-xl bg-indigo-50/70 p-3">
                            <p className="text-sm font-bold text-indigo-800">
                              {pack.aiPoints.toLocaleString(
                                "fr-FR",
                              )}{" "}
                              crédits IA
                            </p>

                            <p className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-500">
                              <ClockIcon
                                aria-hidden
                                size={13}
                              />

                              Valable{" "}
                              {pack.validity} jour
                              {pack.validity > 1
                                ? "s"
                                : ""}
                            </p>
                          </div>

                          <ul className="mt-4 flex-1 space-y-2">
                            {features.map(
                              (feature) => (
                                <li
                                  key={feature}
                                  className="flex items-start gap-2 text-xs leading-5 text-slate-600"
                                >
                                  <CheckCircleIcon
                                    aria-hidden
                                    size={15}
                                    weight="fill"
                                    className="mt-0.5 shrink-0 text-emerald-500"
                                  />

                                  {feature}
                                </li>
                              ),
                            )}
                          </ul>

                          <Button
                            type="button"
                            disabled={
                              selectedPackId !==
                              null
                            }
                            onClick={() => {
                              void choosePack(
                                pack.id,
                              );
                            }}
                            className={cn(
                              "mt-5 h-11 w-full rounded-xl text-sm font-bold",
                              recommended
                                ? "bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 text-white"
                                : "border border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100",
                            )}
                          >
                            {selecting ? (
                              <>
                                <CircleNotchIcon
                                  aria-hidden
                                  size={17}
                                  weight="bold"
                                  className="animate-spin"
                                />

                                Vérification…
                              </>
                            ) : (
                              "Recharger avec ce pack"
                            )}
                          </Button>
                        </motion.article>
                      );
                    },
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};